import * as yup from 'yup';

import { FlightEventReport } from '../../core/api/clients';

import commonValidators from '../../core/validators/commonValidators';
import flightValidators from '../../core/validators/flightValidators';

type FlightEventReportData = Pick<FlightEventReport,
'flightNumber'
| 'aircraftNumber'
| 'departureAirport'
| 'scheduledArrivalAirport'
| 'landingAirport'
| 'captain'
| 'firstOfficer'
| 'flightAttendant'
| 'eventTitle'
| 'locationOfEvent'
| 'eventDate'
| 'localEventTime'
| 'eventType'
| 'eventType2'
| 'eventType3'
| 'typeOfFlight'
| 'operationalImpact'
| 'numberOfPassengers'
| 'flightDeckObserver'
| 'flightDeckObserverInformation'
| 'altitude'
| 'airspeed'
| 'airspeedType'
| 'pilotFlying'
| 'runwayDesignation'
| 'surfaceCondition'
| 'maxGroundSpeed'
| 'phaseOfFlight'
| 'phaseOfFlight2'
| 'emergencyDeclared'
| 'cabinBraced'
| 'seatBeltSign'
| 'evacuation'
| 'overweightLanding'
| 'arffRequested'
| 'arffResponded'
| 'emsRequested'
| 'emsResponded'
| 'securityRequested'
| 'securityResponded'
| 'emergencyMedKitUsed'
| 'aedUsed'
| 'portableOxygenUsed'
| 'aircraftTaxiToGate'
| 'coordinationWithDispatch'
| 'timeOfDay'
| 'windDirection'
| 'windSpeed'
| 'windGusts'
| 'precipitation'
| 'visibility'
| 'rvr'
| 'cloudCeiling'
| 'temperature'
| 'windShear'
| 'lightning'
| 'narrative'
| '$discriminator'
>;
const FlightEventFormValidationSchema: yup.ObjectSchema<FlightEventReportData> = yup.object({
  $discriminator: yup.string().required().meta({ step: 0 }),
  // Flight & Crew
  flightNumber: flightValidators.flightNumber({ label: 'Flight Number', step: 0, optionalIfBlankGenerated: true }),
  aircraftNumber: flightValidators.aircraftNumber({ label: 'Aircraft Number', step: 0, required: true }),

  departureAirport: flightValidators.airport({ label: 'Departure Airport', step: 0, required: true }),
  scheduledArrivalAirport: flightValidators.airport({ label: 'Scheduled Arrival Airport', step: 0, required: true }),
  landingAirport: flightValidators.airport({ label: 'Landing Airport', step: 0, required: true }),

  captain: flightValidators.crewMember({ label: 'Captain', step: 0, optionalIfBlankGenerated: true }),
  firstOfficer: flightValidators.crewMember({ label: 'First Officer', step: 0, optionalIfBlankGenerated: true }),
  flightAttendant: flightValidators.crewMember({
    label: 'Flight Attendant',
    step: 0,
    optionalIfBlankGenerated: true,
    requiredIfRevenue: true,
  }),

  // Event
  eventTitle: commonValidators.nullableString({ label: 'Event Title', step: 1, required: true, maxLength: 75 }),
  locationOfEvent: commonValidators.nullableString({ label: 'Location of Event', step: 1 }),
  eventDate: commonValidators.nullableString({ label: 'Event Date', required: true, step: 1 }),

  localEventTime: commonValidators.time({
    message: 'Local Event Time must be a valid 24-hour time',
    label: 'Local Event Time',
    required: true,
    step: 1,
  }),

  eventType: commonValidators.nullableString({
    label: 'Event Type',
    required: true,
    step: 1,
  }),

  eventType2: commonValidators.nullableString({
    label: 'Event Type 2',
    required: true,
    step: 1,
  }),

  eventType3: commonValidators.nullableString({
    label: 'Event Type 3',
    required: true,
    step: 1,
  }),

  typeOfFlight: commonValidators.nullableString({
    label: 'Type of Flight',
    required: true,
    step: 1,
  }),

  operationalImpact: commonValidators.nullableString({
    label: 'Operational Impact',
    required: true,
    step: 1,
  }),

  numberOfPassengers: yup.number()
    .label('Number of Passengers')
    .typeError('Number of Passengers must be a number')
    .integer()
    .nullable()
    .optional()
    .meta({ step: 1 }),

  flightDeckObserver: yup.string()
    .label('Flight Deck Observer')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  flightDeckObserverInformation: yup.string()
    .label('Flight Deck Observer Information')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  altitude: yup.string()
    .label('Altitude')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  airspeed: yup.string()
    .label('Airspeed')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  airspeedType: yup.string()
    .label('Airspeed Type')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  pilotFlying: yup.string()
    .label('Pilot Flying')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  runwayDesignation: yup.string()
    .label('Runway')
    .max(3)
    .matches(/^(0?[1-9]|[1-2]\d|3[0-6])([LCR])?$/, {
      excludeEmptyString: true,
      message: 'Runway must be a full runway designation (ex. 03R)',
    })
    .nullable()
    .optional()
    .meta({ step: 1 }),

  surfaceCondition: yup.string()
    .label('Surface Condition')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  maxGroundSpeed: yup.string()
    .label('Max Ground Speed')
    .nullable()
    .optional()
    .matches(/^([0-9]|[1-9][0-9]|[1][0-9][0-9]|20[0-0])$/, {
      message: 'Max Ground Speed must be a number between 0 and 200',
      excludeEmptyString: true,
    })
    .meta({ step: 1 }),

  phaseOfFlight: yup.string()
    .label('Phase of Flight')
    .nullable()
    .required()
    .meta({ step: 1 }),

  phaseOfFlight2: yup.string()
    .label('Phase of Flight')
    .nullable()
    .required()
    .meta({ step: 1 }),

  emergencyDeclared: commonValidators.radioGroupOption({ label: 'Emergency Declared', required: true, step: 1 }),
  cabinBraced: commonValidators.radioGroupOption({ label: 'Cabin Braced', required: true, step: 1 }),
  seatBeltSign: commonValidators.radioGroupOption({ label: 'Seat Belt Sign', required: true, step: 1 }),
  evacuation: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 1 }),
  overweightLanding: commonValidators.radioGroupOption({ label: 'Overweight Landing', required: true, step: 1 }),
  arffRequested: commonValidators.radioGroupOption({ label: 'ARFF Requested', required: true, step: 1 }),
  arffResponded: commonValidators.radioGroupOption({ label: 'ARFF Responded', required: true, step: 1 }),
  emsRequested: commonValidators.radioGroupOption({ label: 'EMS Requested', required: true, step: 1 }),
  emsResponded: commonValidators.radioGroupOption({ label: 'EMS Responded', required: true, step: 1 }),
  securityRequested: commonValidators.radioGroupOption({ label: 'Security Requested', required: true, step: 1 }),
  securityResponded: commonValidators.radioGroupOption({ label: 'Security Responded', required: true, step: 1 }),
  emergencyMedKitUsed: commonValidators.radioGroupOption({ label: 'Emergency Med Kit Used', required: true, step: 1 }),
  aedUsed: commonValidators.radioGroupOption({ label: 'AED Used', required: true, step: 1 }),
  portableOxygenUsed: commonValidators.radioGroupOption({ label: 'Portable Oxygen Used', required: true, step: 1 }),
  aircraftTaxiToGate: commonValidators.radioGroupOption({ label: 'Aircraft Taxi to Gate', required: true, step: 1 }),
  coordinationWithDispatch: commonValidators.radioGroupOption({ label: 'Coordination with Dispatch', required: true, step: 1 }),

  // Weather
  timeOfDay: yup.string()
    .label('Time of Day')
    .nullable()
    .optional()
    .meta({ step: 2 }),

  windDirection: yup.string()
    .label('Wind Direction')
    .nullable()
    .optional()
    .matches(/[0-3][0-9][0-9]/, {
      excludeEmptyString: true,
      message: 'Wind direction must be a number between 000 and 360',
    })
    .meta({ step: 2 }),

  windSpeed: yup.string()
    .label('Wind Speed')
    .nullable()
    .optional()
    .matches(/^[0-9]+$/, {
      message: 'Wind Speed must be a number',
      excludeEmptyString: true,
    })
    .meta({ step: 2 }),

  windGusts: yup.string()
    .label('Wind Gusts')
    .nullable()
    .optional()
    .matches(/^[0-9]+$/, {
      message: 'Wind Gusts must be a number',
      excludeEmptyString: true,
    })
    .meta({ step: 2 }),

  precipitation: yup.string()
    .label('Precipitation')
    .nullable()
    .optional()
    .meta({ step: 2 }),

  visibility: yup.string()
    .label('Visibility')
    .nullable()
    .optional()
    .meta({ step: 2 }),

  rvr: yup.string()
    .label('RVR')
    .nullable()
    .optional()
    .meta({ step: 2 }),

  cloudCeiling: yup.string()
    .label('Cloud Ceiling')
    .nullable()
    .optional()
    .meta({ step: 2 }),

  temperature: yup.string()
    .label('Temperature')
    .nullable()
    .optional()
    .matches(/^-?[0-9]+$/, {
      message: 'Temperature must be a number',
      excludeEmptyString: true,
    })
    .meta({ step: 2 }),

  windShear: yup.boolean()
    .label('Wind Shear')
    .nullable()
    .optional()
    .meta({ step: 2 }),

  lightning: yup.boolean()
    .label('Lightning')
    .nullable()
    .optional()
    .meta({ step: 2 }),

  // Narrative
  narrative: yup.string()
    .label('Narrative')
    .nullable()
    .required()
    .meta({ step: 3 }),
});

export default FlightEventFormValidationSchema;
